import { coreGet, coreMutate } from "../../core-api/api-client";

export type ShadowFlowStage =
  | "INQUIRY_READY"
  | "AI_REVIEWED"
  | "SALES_ASSIGNED"
  | "SALES_ACCEPTED"
  | "CASE_CREATED"
  | "SOURCING_ASSIGNED"
  | "SOURCING_ACCEPTED"
  | "SUPPLIER_RESULT_RECORDED"
  | "PRICING_READY"
  | "QUOTE_READY"
  | "WAITING_APPROVAL"
  | "APPROVED"
  | "SENT_SYNTHETIC";

export interface ShadowFlowView {
  id: string;
  stage: ShadowFlowStage;
  version: number;
  scenario: string;
  originalInquiryHash: string;
  currentOriginalInquiryHash: string;
  originalInquiryUnchanged: boolean;
  shadowInquiry: {
    id: string;
    publicId: string;
    product: string | null;
    cas: string | null;
    quantity: string | null;
    specification: string | null;
    contactName: string | null;
    companyName: string | null;
    email: string;
    phone: string | null;
    isSynthetic: boolean;
    doNotContact: boolean;
    outboundMode: "SYNTHETIC_ONLY";
    sourceInquiryReference: string;
    originalRecordImmutable: boolean;
  };
  ai: {
    runIds: string[];
    reviewDecision: string | null;
    manualOnly: true;
    autoApply: false;
  };
  salesAssignment: AssignmentView | null;
  sourcingAssignment: (AssignmentView & { derivedStatus?: string }) | null;
  customerOrganizationId: string | null;
  contactId: string | null;
  caseId: string | null;
  supplierRfqId: string | null;
  supplierOffer: {
    id: string;
    currency: string;
    leadTimeDays: number | null;
    validUntil: string | null;
    supplier?: { displayName: string };
    lines: Array<{ unitCost?: string; technicalDeviation?: string | null }>;
  } | null;
  costSnapshotId: string | null;
  pricingHandoff: {
    product?: string;
    specification?: string;
    quantity?: string;
    unit?: string;
    approvedCostBasisCny?: string;
    leadTime?: string;
    validity?: string;
    technicalDeviation?: string;
    costSnapshotId?: string;
    markupFactor?: string;
    fxSnapshot?: string;
    sellingPriceCny?: string;
    customerUnitPriceUsd?: string;
    roundingPolicy?: string;
    quoteVersion?: number;
  } | null;
  customerQuoteId: string | null;
  quoteVersion: {
    id: string;
    versionNumber: number;
    status: string;
    currency: string;
    total: string | null;
    validUntil: string;
    paymentTerms: string;
    deliveryTerms: string;
    lines: Array<{
      description: string;
      productReference: string | null;
      quantity: string;
      unit: string;
      unitPrice: string;
      lineTotal: string;
    }>;
  } | null;
  approvalRequestId: string | null;
  syntheticDeliveryId: string | null;
  timeline: Array<{
    id: string;
    sequence: number;
    action: string;
    occurredAt: string;
  }>;
  permissions: {
    canReviewAI: boolean;
    canDispatch: boolean;
    canAcceptSales: boolean;
    canCreateCase: boolean;
    canAssignSourcing: boolean;
    canAcceptSourcing: boolean;
    canRecordSupplierQuote: boolean;
    canPreparePricing: boolean;
    canCreateQuote: boolean;
    canSubmitApproval: boolean;
    canApprove: boolean;
    canSyntheticSend: boolean;
    canReadSupplierCost: boolean;
    canReadSupplierIdentity: boolean;
  };
}

interface AssignmentView {
  id: string;
  status: string;
  primaryAssigneeId: string | null;
  assignedAt: string;
}

export interface LocalUatAccountDirectory {
  accounts: Array<{
    username: string;
    status: string;
    roles: string[];
    permissions: string[];
    readOnly: boolean;
  }>;
}

export function loadShadowFlow(signal?: AbortSignal): Promise<ShadowFlowView> {
  return coreGet<ShadowFlowView>("/shadow-flow", signal);
}

export function loadLocalUatAccounts(signal?: AbortSignal) {
  return coreGet<LocalUatAccountDirectory>(
    "/auth/local-uat/password-accounts",
    signal,
  );
}

export function performShadowAction(
  flowId: string,
  action:
    | "assign-sales"
    | "accept-sales"
    | "create-case"
    | "assign-sourcing"
    | "accept-sourcing"
    | "record-supplier-result"
    | "prepare-pricing"
    | "create-customer-quote"
    | "submit-approval"
    | "approve"
    | "synthetic-send",
): Promise<ShadowFlowView> {
  return coreMutate<ShadowFlowView>(
    `/shadow-flow/${flowId}/${action}`,
    "POST",
    {},
  );
}

export function confirmAIReview(flowId: string, runIds: readonly string[]) {
  return coreMutate<ShadowFlowView>(
    `/shadow-flow/${flowId}/ai-review`,
    "POST",
    {
      runIds,
      manualContinuation: false,
    },
  );
}

export function continueWithoutAI(flowId: string) {
  return coreMutate<ShadowFlowView>(
    `/shadow-flow/${flowId}/ai-review`,
    "POST",
    {
      runIds: [],
      manualContinuation: true,
      reason:
        "Authorized UAT operator continued manually because AI was unavailable or failed.",
    },
  );
}

export interface RealShadowBatchResult {
  mode: "REAL_DATA_READ_ONLY_SHADOW";
  status: string;
  batchId: string;
  batchType: "BATCH_0" | "BATCH_1" | "BATCH_2" | "BATCH_3";
  sampleTarget: number;
  created: number;
  existing: number;
  conflicts: number;
  sourceWrites: 0;
  businessMutations: 0;
  transport: {
    httpStatus: number;
    responseBytes: number;
    schemaFingerprint?: string;
    itemCount: number;
  };
}

export interface RealShadowListItem {
  id: string;
  anonymousReference: string;
  sourceSite: string;
  sourceTimestamp: string;
  emailCategory:
    | "PERSONAL"
    | "ACADEMIC_INSTITUTIONAL"
    | "CORPORATE"
    | "UNKNOWN";
  product: string | null;
  cas: string | null;
  quantity: string | null;
  organizationResolution: {
    status: "RESOLVED" | "CANDIDATE" | "CONFLICT" | "UNKNOWN";
    organizationType: string;
    method: string;
    confidence: "HIGH" | "MEDIUM" | "LOW";
    evidence: string[];
  };
  customerResolution: {
    recommendedGrade: string;
    confidence: string;
    evidence: string[];
  };
  fieldValidation: Array<{
    field: string;
    state: string;
    blocking: boolean;
    provenance: string;
  }>;
  recommendation: {
    shadowRecommendationType: string;
    confidence: string;
    missingInformation: {
      internallyResolvableFields: string[];
      nonBlockingMissingFields: string[];
      blockingMissingFields: string[];
    };
    risk: { level: string; ruleHits: string[]; blocking: boolean };
    caseCandidates: Array<{ anonymousReference: string; confidence: string }>;
    sourcingHistory: { type: string; recommendation: string };
  };
  reviewStatus: string;
}

export interface RealShadowListResponse {
  mode: "REAL_DATA_READ_ONLY_SHADOW";
  total: number;
  items: RealShadowListItem[];
}

export function loadRealShadowInquiries(signal?: AbortSignal) {
  return coreGet<RealShadowListResponse>("/real-shadow/inquiries", signal);
}

export function collectRealShadowBatch0() {
  return coreMutate<RealShadowBatchResult>(
    "/real-shadow/collect",
    "POST",
    { batchId: "real-shadow-26b-batch-0", batchType: "BATCH_0", maxRecords: 1 },
    "real-shadow-26b-batch-0",
  );
}
